"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Shell = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const web_blizzard_reporting_1 = require("@snapchat/web-blizzard-reporting");
const web_experiments_1 = require("@snapchat/web-experiments");
const web_head_meta_1 = require("@snapchat/web-head-meta");
const web_i18n_1 = require("@snapchat/web-i18n");
const web_sc_cookies_1 = require("@snapchat/web-sc-cookies");
const react_1 = require("react");
const react_intl_1 = require("react-intl");
const pageContext_1 = require("./pageContext");
const webConfigContext_1 = require("./webConfigContext");
const media_query_1 = require("../../media-query");
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const web_sentry_1 = require("@snapchat/web-sentry");
const web_popup_1 = require("@snapchat/web-popup");
const web_blizzard_reporting_hooks_1 = require("@snapchat/web-blizzard-reporting-hooks");
const web_env_1 = require("@snapchat/web-env");
// TODO @ebanjo: extend BaseShell instead.
/**
 * Common page wrapper for Consumer web apps.
 * Mainly handles:
 * - WPV & WEB_SESSION reporting
 * - AB initialisation, and
 * - Loading page helmet
 * - Loading localised messages
 */
function Shell(props) {
    (0, web_sc_cookies_1.useSCWebSession)();
    const { locale, messages, country, webClientId = "", onGrafanaMetricsReady, shareId, webConfigNamespaces, sentryOrigin, sentryDsnUri, skipWebConfigNamespaceProviderInitialization, webPageViewSource, isEmbed, webPageViewTraceId, } = props;
    const webPageLoadMetricsSent = (0, react_1.useRef)(false);
    const [mounted, setMounted] = (0, react_1.useState)(Boolean(props.mountByDefault));
    (0, react_1.useEffect)(() => {
        (0, web_blizzard_reporting_hooks_1.initBlizzardWebClient)({
            isProd: (0, web_env_1.isProd)(),
            withWebPageOpen: !isEmbed,
            isGDPRCountry: props.isGDPRCountry,
            metricsAppId: web_blizzard_reporting_1.PageType[props.pageMetadata.pageType],
        });
    }, [props.isGDPRCountry, props.pageMetadata.pageType, isEmbed]);
    // its important to ensure Blizzard client is initiated before we call this
    (0, web_blizzard_reporting_hooks_1.useWebPageOpen)();
    (0, react_1.useEffect)(() => {
        setMounted(true);
        if (!skipWebConfigNamespaceProviderInitialization) {
            void (0, web_experiments_1.initConfigsClient)();
        }
        void (0, web_graphene_reporting_1.initMetrics)(onGrafanaMetricsReady);
        if (sentryOrigin && sentryDsnUri) {
            void (0, web_sentry_1.initSentry)(sentryOrigin, sentryDsnUri);
        }
    }, [
        onGrafanaMetricsReady,
        sentryOrigin,
        sentryDsnUri,
        skipWebConfigNamespaceProviderInitialization,
    ]);
    (0, react_1.useEffect)(() => {
        if (!webPageLoadMetricsSent.current && webClientId) {
            webPageLoadMetricsSent.current = true;
            (0, web_blizzard_reporting_1.reportWebPageView)({
                webClientId,
                traceId: webPageViewTraceId,
                pageType: props.pageMetadata.pageType,
                source: webPageViewSource,
            });
        }
    }, [props.pageMetadata.pageType, webClientId, webPageViewTraceId, webPageViewSource]);
    const isMobile = (0, media_query_1.useMobileMediaQuery)();
    const pageType = props.pageMetadata.pageType;
    const snapchatDeepLinkUrl = props.snapchatDeepLinkUrl && webClientId
        ? (0, web_sc_cookies_1.addWebClientIdToURL)(props.snapchatDeepLinkUrl, webClientId)
        : props.snapchatDeepLinkUrl;
    const main = mounted ? props.children : null;
    const pageContextProps = (0, react_1.useMemo)(() => ({
        isMobile,
        locale,
        pageType,
        webClientId,
        country,
        shareId,
        traceId: props.webPageViewTraceId,
    }), [isMobile, locale, pageType, webClientId, country, shareId, props.webPageViewTraceId]);
    const headMetaProps = {
        isProd: (0, web_env_1.isProd)(),
        direction: props.direction,
        title: props.title,
        policyDirectives: props.policyDirectives,
        hrefLangTagOptions: props.hrefLangTagOptions,
        description: props.description,
        metricsPageName: props.metricsPageName,
        snapchatDeepLinkUrl: snapchatDeepLinkUrl,
        canonicalUrl: props.canonicalUrl,
        robotsMeta: props.robotsMeta,
        gaid: props.gaid,
        lang: locale,
        manifestPath: props.manifestPath,
        preloads: props.preloads,
        preconnects: props.preconnects,
        theme: props.theme,
        videoMetadata: props.videoMetadata,
    };
    // TODO (https://jira.sc-corp.net/browse/WEB-2231): remove this check after A/B is completed
    if (skipWebConfigNamespaceProviderInitialization) {
        return ((0, jsx_runtime_1.jsx)(react_intl_1.IntlProvider, { locale: locale, defaultLocale: web_i18n_1.DEFAULT_LOCALE, messages: messages, children: (0, jsx_runtime_1.jsxs)(web_popup_1.PopupProvider, { children: [props.linkPreview && (0, jsx_runtime_1.jsx)(web_head_meta_1.LinkPreviewHeadMeta, { linkPreview: props.linkPreview }), (0, jsx_runtime_1.jsx)(web_head_meta_1.HeadMeta, { ...headMetaProps }), (0, jsx_runtime_1.jsx)(pageContext_1.PageContextProvider, { value: pageContextProps, children: main })] }) }));
    }
    return ((0, jsx_runtime_1.jsx)(react_intl_1.IntlProvider, { locale: locale, defaultLocale: web_i18n_1.DEFAULT_LOCALE, messages: messages, children: (0, jsx_runtime_1.jsx)(web_popup_1.PopupProvider, { children: (0, jsx_runtime_1.jsxs)(webConfigContext_1.webConfigContext.Provider, { experimentNamespaces: webConfigNamespaces, webClientId: webClientId, countryCode: country, children: [props.linkPreview && (0, jsx_runtime_1.jsx)(web_head_meta_1.LinkPreviewHeadMeta, { linkPreview: props.linkPreview }), (0, jsx_runtime_1.jsx)(web_head_meta_1.HeadMeta, { ...headMetaProps }), (0, jsx_runtime_1.jsx)(pageContext_1.PageContextProvider, { value: pageContextProps, children: main })] }) }) }));
}
exports.Shell = Shell;
//# sourceMappingURL=Shell.js.map